import { AxiosError } from 'axios';
import { put, call, takeLatest } from 'redux-saga/effects';
import {
  types,
  LoginRequest,
  UpdateCurrentAdminRequest,
  ResetPasswordRequest,
  RequestResetPasswordRequest
} from './types';
import { API } from 'services/api/base';
import { getErrorCode } from 'services/api/normalizer';
import { Admin } from 'store/admins';

export function* loginAsync({ payload }: LoginRequest) {
  const { email, password } = payload;
  yield put({ type: types.LOGIN.PENDING, payload: true });

  try {
    const loginApi = new API('login');
    const adminApi = new API('current');
    const { token }: { token: string } = yield call(loginApi.create, { email, password });
    localStorage.setItem('token', token);

    const admin: Admin = yield call(adminApi.getAll);
    yield put({ type: types.LOGIN.SUCCESS, payload: { token, admin } });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.LOGIN.ERROR, payload: 'Invalid email and/or password' });
    } else if (errorCode >= 500) {
      yield put({ type: types.LOGIN.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.LOGIN.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.LOGIN.PENDING, payload: false });
  }
}

export function* updateCurrentAdminAsync({ payload }: UpdateCurrentAdminRequest) {
  yield put({ type: types.UPDATE_CURRENT_ADMIN.PENDING, payload: true });
  payload.id = 'current';

  try {
    const adminsApi = new API('');
    const admin: Admin = yield call(adminsApi.update, payload);

    yield put({ type: types.UPDATE_CURRENT_ADMIN.SUCCESS, payload: admin });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_CURRENT_ADMIN.ERROR, payload: 'Invalid data' });
    } else {
      yield put({ type: types.UPDATE_CURRENT_ADMIN.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.UPDATE_CURRENT_ADMIN.PENDING, payload: false });
  }
}

export function* requestResetPasswordAsync({ payload }: RequestResetPasswordRequest) {
  yield put({ type: types.REQUEST_RESET_PASSWORD.PENDING, payload: true });

  try {
    const adminApi = new API('passwordreset');

    yield call(adminApi.create, { email: payload });

    yield put({ type: types.REQUEST_RESET_PASSWORD.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode === 403) {
      yield put({
        type: types.REQUEST_RESET_PASSWORD.ERROR,
        payload: "Cannot reset this user's password"
      });
    } else if (errorCode === 404) {
      yield put({
        type: types.REQUEST_RESET_PASSWORD.ERROR,
        payload: 'This email has not been registered in our system. Please try again.'
      });
    } else {
      yield put({
        type: types.REQUEST_RESET_PASSWORD.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.REQUEST_RESET_PASSWORD.PENDING, payload: false });
  }
}

export function* resetPasswordAsync({ payload }: ResetPasswordRequest) {
  yield put({ type: types.RESET_PASSWORD.PENDING, payload: true });

  try {
    const adminApi = new API(`passwordreset/${payload.token}`);

    yield call(adminApi.create, { password: payload.password });

    yield put({ type: types.RESET_PASSWORD.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode === 400) {
      yield put({
        type: types.RESET_PASSWORD.ERROR,
        payload: 'This token has expired.'
      });
    } else if (errorCode === 404) {
      yield put({
        type: types.RESET_PASSWORD.ERROR,
        payload: 'Invalid token.'
      });
    } else {
      yield put({
        type: types.RESET_PASSWORD.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.RESET_PASSWORD.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.LOGIN.REQUEST, loginAsync),
  takeLatest(types.UPDATE_CURRENT_ADMIN.REQUEST, updateCurrentAdminAsync),
  takeLatest(types.RESET_PASSWORD.REQUEST, resetPasswordAsync),
  takeLatest(types.REQUEST_RESET_PASSWORD.REQUEST, requestResetPasswordAsync)
];
